import { useMemo, useState } from "react";
import { addToast, Alert, Button, Card, CardBody, CardHeader, Input } from "@heroui/react";

import { unlockVault } from "../utils/vault";
import { hooks } from "@/services/api/hooks";
import { useBoundStore } from "@/app/store";
import { useShallow } from "zustand/shallow";
import { LockIcon } from "@phosphor-icons/react";
import { ICON_SIZE } from "@/config/constants";
import { usePageLayout } from "@/app/hooks/usePageLayout";

interface AccessEncryptionProps {
  onSuccess: () => void;
}

export function AccessEncryption({ onSuccess }: AccessEncryptionProps) {
  const [isUnlocking, setIsUnlocking] = useState(false);
  const { userId } = useBoundStore(useShallow((state) => ({ userId: state.user.id })));
  const { password, handleChange, resetForm, errors, validateForm } = useAccessEncryptionForm();
  const { data: vaultData } = hooks.vaultKeys.useVaultKeyByUserId(userId);

  const handleUnlockVault = async () => {
    if (!validateForm()) return;

    if (!vaultData) {
      addToast({
        title: "Error",
        description: "Vault data not found. Please try again.",
        color: "danger",
      });
      return;
    }

    setIsUnlocking(true);

    try {
      // Unlock the vault with the provided master password
      const dek = await unlockVault(password, {
        salt: vaultData.salt,
        wrapped: vaultData.wrapped,
        iv: vaultData.iv,
      });

      if (!dek) {
        addToast({
          title: "Invalid password",
          description: "The master password you entered is incorrect.",
          color: "danger",
        });
        return;
      }

      addToast({
        title: "Vault unlocked successfully",
        description: "Your encryption vault has been unlocked. Your data is now accessible.",
        color: "success",
      });

      resetForm();
      onSuccess();
    } catch (error) {
      console.error("Error unlocking vault:", error);
      addToast({
        title: "Error unlocking vault",
        description: "The master password you entered is incorrect, or there was an error unlocking the vault.",
        color: "danger",
      });
    } finally {
      setIsUnlocking(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleUnlockVault();
    }
  };

  const pageLayoutConfig = useMemo(() => ({ navbar: { hideBack: true, title: "InkRows" } }), []);
  usePageLayout(pageLayoutConfig);

  return (
    <>
      <Card className="top-1/2 left-1/2 max-w-sm -translate-x-1/2 -translate-y-1/2">
        <CardHeader>Access Encrypted Data</CardHeader>
        <CardBody>
          <div className="space-y-4">
            <Alert
              color="primary"
              title="Enter your master password"
              description="Your data is encrypted. Enter your master password to access your notes, todos, tags, and folders on this device."
            />
            <Input
              autoComplete="current-password"
              errorMessage={errors.password}
              isDisabled={isUnlocking}
              isInvalid={!!errors.password}
              label="Master Password"
              name="password"
              placeholder="Enter your master password"
              size="sm"
              type="password"
              value={password}
              onValueChange={(value) => handleChange(value)}
              onKeyDown={handleKeyDown}
            />

            <Button fullWidth color="primary" onPress={handleUnlockVault} isLoading={isUnlocking}>
              {isUnlocking ? null : <LockIcon size={ICON_SIZE.md} />} Unlock
            </Button>
          </div>
        </CardBody>
      </Card>
    </>
  );
}

function useAccessEncryptionForm() {
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ password?: string }>({});

  const handleChange = (value: string) => {
    setPassword(value);
    setErrors((prev) => ({ ...prev, password: undefined }));
  };

  const validateForm = () => {
    const newErrors: { password?: string } = {};

    if (!password) {
      newErrors.password = "Master password is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setPassword("");
    setErrors({});
  };

  return { password, handleChange, resetForm, errors, validateForm };
}
